import React from "react";
import ReactDOM from "react-dom/client";
import { MsalProvider } from "@azure/msal-react";
import {
  BrowserCacheLocation,
  PublicClientApplication,
} from "@azure/msal-browser";
import "./w3.css";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import { AppContextProvider } from "./context/app.context";
// ModelRwContextProvider removed - Realm EOL Jan 13, 2026
// File kept for reference: ./context/modelRW.context.js
import { ModelTsContextProvider } from "./context/modelTS.context";
import { ScheduleContextProvider } from "./context/schedule.context";
// DISABLED: LogRocket subscription suspended
// import LogRocket from "logrocket";
// import setupLogRocketReact from "logrocket-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// ReactQueryDevtools moved to App.js - conditional on user's devTools setting
import { APP_VERSION } from "./utility/keys";

// Log app version immediately on load
console.log(
  "%c🚀 Scheduler App Version: " + APP_VERSION,
  "color: #00FF00; font-weight: bold; font-size: 14px; background-color: #1a1a1a; padding: 4px 8px; border-radius: 4px;"
);

// DISABLED: LogRocket subscription suspended
// LogRocket.init("pubdu4/scheduler");
// // after calling LogRocket.init()
// setupLogRocketReact(LogRocket);

// MSAL configuration - Microsoft Entra (Azure AD) Authentication
// App Registration: "Scheduler" in Azure Portal
// clientId: Application (client) ID from Azure app registration
// authority: Directory (tenant) ID - Keller Interiors tenant
//
// IMPORTANT - Client Secret "Scheduler_JWT":
// - This React SPA uses PublicClientApplication (OAuth implicit/PKCE flow)
// - SPAs DO NOT use client secrets in frontend code (security - would be exposed in browser)
// - The "Scheduler_JWT" client secret is stored ELSEWHERE (AWS Lambda, backend API, or Azure Key Vault)
// - Client secret expires periodically (e.g., every 24 months) and must be renewed in Azure Portal
// - When expired, backend token validation fails (users can't authenticate)
// - To renew: Azure Portal > App Registrations > Scheduler > Certificates & secrets > New client secret
// - Update the secret in the backend service (Lambda environment variables, Key Vault, etc.) - NOT here
//
// Azure Portal: https://portal.azure.com > Microsoft Entra ID > App Registrations > Scheduler
const configuration = {
  auth: {
    clientId: "4ae8c1ac-41df-46bb-a881-011a10d1b09b",
    authority:
      "https://login.microsoftonline.com/3d199282-cde8-4234-bab6-ef614f1313af",
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },
};

const msalInstance = new PublicClientApplication(configuration);

msalInstance.initialize().then(() => {
  console.log("index.js MSAL initialize");
  // Default to using the first account if no account is active on page load

  if (
    !msalInstance.getActiveAccount() &&
    msalInstance.getAllAccounts().length > 0
  ) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  // Create a QueryClient instance
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 1000, // 5 seconds: data is fresh for 5 seconds
        cacheTime: 30 * 1000, // 30 seconds: data stays in cache for 30 seconds after becoming stale
        retry: 2, // Retry failed queries up to 2 times
        refetchOnWindowFocus: false, // Disabled: queries controlled by shouldFetch flags
        refetchOnReconnect: false, // Disabled: queries controlled by shouldFetch flags
        refetchOnMount: false, // Disabled: queries controlled by shouldFetch flags
        refetchInterval: false, // Disabled: queries run once when triggered, no polling
        onError: (error) => {
          console.error("Query error:", error); // Global error handling for queries
        },
      },
      mutations: {
        onError: (error) => {
          console.error("Mutation error:", error); // Global error handling for mutations
        },
      },
    },
  });

  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    // <React.StrictMode>       NOTE: "React intentionally remounts your components in development"
    <BrowserRouter>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <AppContextProvider>
            <ModelTsContextProvider>
              <ScheduleContextProvider>
                <App />
              </ScheduleContextProvider>
            </ModelTsContextProvider>
          </AppContextProvider>
        </QueryClientProvider>
      </MsalProvider>
    </BrowserRouter>
    // </React.StrictMode>
  );
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
